import { useState } from 'react';
import { useParams } from 'react-router';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseRoleQuery } from '#api/roles.queries.ts';
import { usePrefetchMaxTagsQuery } from '#api/tags.queries.ts';
import { DeleteIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useReturnNavigation } from '#hooks/useAppLocation.ts';
import { DeleteRoleDialog } from '#screens/roles/delete/DeleteRoleDialog.tsx';
import { SKIP_UNSAVED_CHANGES_PROMPT } from '#screens/roles/detail/form/BlockNavigationDialog.tsx';
import { fromArray, Titled } from '#util/title.ts';
import { RoleDetailForm } from '../form/RoleDetailForm.tsx';
import styles from './EditRolePage.module.css';

/**
 * Used for both adding a new role, as well as editing an existing role.
 *
 * Therefore, roleId may or may not be defined.
 */
export function EditRolePage() {
  const { roleId } = useParams() as { roleId: string };
  const [isDeleting, setIsDeleting] = useState(false);
  const returnTo = useReturnNavigation('/roles');
  const {
    data: { data: role },
    error,
    isFetching,
  } = useSuspenseRoleQuery(roleId);

  {
    // prefetch for InlineTagsList
    usePrefetchMaxTagsQuery();
  }

  // Check if this has been deleted (e.g. in a different tab)
  if (error && !isFetching && error instanceof MissingResourceError) {
    throw error;
  }

  return (
    <Titled title={(title) => fromArray([role.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to="/roles" className="a">
                  Roles
                </Link>
                {' / '}
                {role.name}
              </>
            }
            subtitle={
              <span className={styles.EditRolePage_subtitle}>
                <span className="mb-8">
                  ID: <CopyPill value={role.id} />
                </span>
                <span className="mb-8">
                  Assigned to{' '}
                  {role.hostCount > 0 ?
                    <Link className="a" to={`/hosts?filter.roleID=${role.id}`}>
                      {role.hostCount} {role.hostCount === 1 ? 'host' : 'hosts'}
                    </Link>
                  : '0 hosts'}
                </span>
              </span>
            }
          >
            <ButtonGroup>
              <ButtonGroupItem>
                <Tooltip
                  content="Delete"
                  render={<Button size="small" variant="icon" onClick={() => setIsDeleting(true)} />}
                >
                  <DeleteIcon aria-hidden="true" />
                  <span className="visually-hidden">Delete</span>
                </Tooltip>
              </ButtonGroupItem>
            </ButtonGroup>
          </PageHeader>
        }
      >
        <RoleDetailForm
          roleId={roleId}
          defaultValues={role}
          onSave={() => {
            returnTo();
          }}
        />
        {isDeleting && (
          <DeleteRoleDialog
            roleID={roleId}
            onCancel={() => {
              setIsDeleting(false);
            }}
            onDelete={() => {
              returnTo({ state: SKIP_UNSAVED_CHANGES_PROMPT });
            }}
          />
        )}
      </MainLayout>
    </Titled>
  );
}
